import React, { useState, useRef, useEffect, useMemo } from 'react';
import { View, Button, Flex, Text, useTheme } from '@aws-amplify/ui-react';
import { generateClient } from 'aws-amplify/api';
import { createPhoto } from '../graphql/mutations';
import { useAuth } from '../AuthContext';
import StorageUpgradeModal from './StorageUpgradeModal';
import { PhotoStatus, StorageTier } from '../API';
import './PhotoUpload.css';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Film, Upload, X, Camera, FileImage, AlertTriangle, Image as ImageIcon, HardDrive, Video } from 'lucide-react';
import { getPhotoUploadURL } from '../graphql/queries';

const client = generateClient();

// Security constants
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
  'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-m4v'
];
const MAX_FILES = 50;
const MAX_CONCURRENT_UPLOADS = 3;

// Props interface
interface PhotoUploadProps {
  tripId: string;
  onPhotoUploaded: (newPhoto: any) => void;
  onClose: () => void;
  userRole: string | null;
  onStorageLimitExceeded?: () => void;
}

interface FileWithPreview extends File {
  preview?: string;
  error?: string;
  fileSize?: number;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({ tripId, onPhotoUploaded, onClose, userRole, onStorageLimitExceeded }) => {
  const { user } = useAuth();
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [storageLimitExceeded, setStorageLimitExceeded] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Progress tracking
  const [totalProgress, setTotalProgress] = useState<number>(0);
  const [uploadedFiles, setUploadedFiles] = useState<number>(0);
  const [totalFiles, setTotalFiles] = useState<number>(0);

  // Helper function to check if file is a video
  const isVideoFile = (file: File): boolean => {
    return file.type.startsWith('video/');
  };

  // Memoized file preview generation (lightweight)
  const filePreviews = useMemo(() => {
    return files.map(file => {
      // For image and video files, try to create a quick object URL
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        try {
          return URL.createObjectURL(file);
        } catch {
          return null;
        }
      }
      return null;
    });
  }, [files]);

  // Cleanup previews
  useEffect(() => {
    return () => {
      filePreviews.forEach(preview => {
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [filePreviews]);

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleUpgradeClick = () => {
    // Close the upload modal
    onClose();

    // Trigger the storage upgrade modal
    if (onStorageLimitExceeded) {
      onStorageLimitExceeded();
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);

      // Simulate file input event
      const fileSelectEvent = {
        target: {
          files: droppedFiles
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      await handleFileSelect(fileSelectEvent);
    }
  };

  // Validate and select files with minimal processing
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const selectedFiles = Array.from(e.target.files);
    const newErrors: string[] = [];

    // Security check - limit files
    if (selectedFiles.length > MAX_FILES) {
      newErrors.push(`You can only upload up to ${MAX_FILES} files at once.`);
      setErrors(newErrors);
      return;
    }

    // Validate each file with minimal processing
    const validFiles: FileWithPreview[] = selectedFiles.filter(file => {
      // Validate file type and size
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        newErrors.push(`File "${file.name}" is not a supported image or video type.`);
        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        newErrors.push(`File "${file.name}" exceeds the maximum allowed size (100MB).`);
        return false;
      }

      return true;
    }) as FileWithPreview[];

    // Update state
    setFiles(prevFiles => [...prevFiles, ...validFiles]);
    setErrors(newErrors);
  };

  // Upload a single file with progress tracking
  const uploadFile = async (file: FileWithPreview, index: number): Promise<string | null> => {
    try {
      // Get secure upload URL
      const session = await fetchAuthSession();
      const identityId = session.identityId;
      if (!identityId) {
        console.error('Failed to retrieve identity ID from auth session');
        throw new Error("Authentication error: Failed to retrieve identity ID");
      }

      const response: any = await client.graphql({
        query: getPhotoUploadURL,
        variables: {
          tripId,
          fileName: file.name,
          contentType: file.type || 'image/jpeg', // Fallback type
          identityId: identityId,
          fileSize: file.size
        },
        authMode: "userPool"
      });

      const { uploadURL, photoId } = response.data.getPhotoUploadURL;
      const fileNameParts = file.name.split('.');
      const fileExtension = fileNameParts.length > 1 ? fileNameParts.pop()?.toLowerCase() || 'jpg' : 'jpg';

      // Upload to S3 with progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            // Calculate this file's contribution to total progress
            const fileProgressPercent = Math.round((event.loaded / event.total) * 100);
            const fileContribution = fileProgressPercent / totalFiles;

            setTotalProgress(current => {
              // Calculate new total progress
              const existingProgress = (uploadedFiles / totalFiles) * 100;
              const newTotalProgress = existingProgress + fileContribution;

              // Cap at 99% until completely done
              return Math.min(Math.round(newTotalProgress), 99);
            });
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.open('PUT', uploadURL);
        xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
        xhr.send(file);
      });

      // Create photo record in database
      const photoData = {
        id: photoId,
        tripId: tripId,
        fileName: file.name,
        s3Key: `protected/trips/${tripId}/${photoId}.${fileExtension}`,
        status: PhotoStatus.UPLOADED,
        uploadedBy: user?.userId || user?.username || user?.sub,
        fileSize: file.size,
        contentType: file.type || 'image/jpeg'
      };

      const newPhoto: any = await client.graphql({
        query: createPhoto,
        variables: { input: photoData },
        authMode: "userPool"
      });

      // Notify parent component of successful upload
      onPhotoUploaded(newPhoto.data.createPhoto);

      // Update progress counter for completed file
      setUploadedFiles(prev => prev + 1);

      return photoId;
    } catch (error: any) {
      console.error('Error uploading file:', error);

      // Extract the actual error message from the GraphQL response
      let errorMessage = 'Unknown error occurred';

      // Check if it's a GraphQL error response
      if (error.errors && error.errors.length > 0) {
        errorMessage = error.errors[0].message;
      } else if (error.message) {
        // Fallback to regular error message if available
        errorMessage = error.message;
      }

      // Check if this is a storage limit error
      if (errorMessage.includes('Storage limit exceeded')) {
        setErrors(prevErrors => [
          ...prevErrors,
          `Storage limit exceeded.`
        ]);

        const isCreator = userRole === "CREATOR";
        // If user is the creator, show the upgrade modal
        if (isCreator) {
          setStorageLimitExceeded(true);
        }

      } else {
        // Add generic error
        setErrors(prevErrors => [
          ...prevErrors,
          `Failed to upload ${file.name}: ${errorMessage}`
        ]);
      }

      return null;
    }
  };

  // Upload files with concurrency control
  const uploadFilesWithConcurrency = async (filesToUpload: FileWithPreview[], maxConcurrent: number) => {
    const results: (string | null)[] = [];
    let inProgress = 0;
    let nextIndex = 0;

    const startNext = async () => {
      if (nextIndex >= filesToUpload.length) return;

      const currentIndex = nextIndex++;
      inProgress++;

      try {
        results[currentIndex] = await uploadFile(filesToUpload[currentIndex], currentIndex);
      } finally {
        inProgress--;
        if (nextIndex < filesToUpload.length) {
          // Start next upload
          startNext();
        }
      }
    };

    // Start initial batch of uploads
    const initialBatch = Math.min(maxConcurrent, filesToUpload.length);
    const startPromises = [];

    for (let i = 0; i < initialBatch; i++) {
      startPromises.push(startNext());
    }

    // Wait for all uploads to complete
    await Promise.all(startPromises);

    // Wait until all in-progress uploads are done
    while (inProgress > 0) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return results;
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setTotalProgress(0);
    setUploadedFiles(0);
    setTotalFiles(files.length);
    setErrors([]);

    try {
      // Upload files with concurrency limit
      const results = await uploadFilesWithConcurrency(files, MAX_CONCURRENT_UPLOADS);

      // Count successful uploads
      const successCount = results.filter(Boolean).length;
      const failCount = files.length - successCount;

      // Set progress to 100% when done
      setTotalProgress(100);

      // Cleanup: Revoke all object URLs before clearing files
      files.forEach(file => {
        if (file.preview && file.preview.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });

      // Reset files only if all were successful
      if (failCount === 0) {
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onClose(); // Add this line to close the modal

      }
    } catch (error) {
      console.error('Error during upload:', error);
      setErrors([...errors, 'There was an unexpected error while uploading photos. Please try again.']);
    } finally {
      setUploading(false);
    }
  };

  return (
    <View className="photo-upload-container">
      <div
        className="dropzone cinematic-dropzone"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileSelect}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />

        {files.length === 0 ? (
          <div className="dropzone-placeholder">
            <div className="upload-icon">
              <Film size={32} className="mb-3 text-blue-400" />
            </div>
            <Text className="text-gray-300 font-medium uppercase tracking-wider text-sm mb-1">
              Drag photos & videos here or click to browse
            </Text>
            <Text fontSize="0.8rem" color="gray.500">
              Images: JPG, PNG, GIF, WEBP, HEIC | Videos: MP4, MOV, AVI, M4V (max 100MB)
            </Text>
          </div>
        ) : (
          <div className="selected-files">
            <Text fontWeight="bold" color="#BBBBBB" className="uppercase tracking-wider text-sm mb-3 flex items-center">
              <Camera size={16} className="mr-2 text-blue-400" />
              {files.length} {files.length === 1 ? 'file' : 'files'} selected
            </Text>
            <Flex direction="column" gap="0.5rem" marginTop="1rem">
              {files.map((file, index) => (
                <Flex
                  key={index}
                  direction="row"
                  alignItems="center"
                  backgroundColor="#252525"
                  padding="0.5rem"
                  borderRadius="4px"
                >
                  {/* Lightweight preview */}
                  <View
                    width="40px"
                    height="40px"
                    marginRight="0.75rem"
                    backgroundColor="#333"
                    display="flex"
                    borderRadius="4px"
                    position="relative"
                  >
                    {filePreviews[index] ? (
                      isVideoFile(file) ? (
                        <>
                          <video
                            src={filePreviews[index] || ''}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                          />
                          <View
                            position="absolute"
                            top="50%"
                            left="50%"
                            style={{ transform: 'translate(-50%, -50%)' }}
                          >
                            <Video size={16} color="#fff" />
                          </View>
                        </>
                      ) : (
                        <img
                          src={filePreviews[index] || ''}
                          alt={file.name}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      )
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%'
                        }}
                      >
                        {isVideoFile(file) ? (
                          <Video size={20} color="#666" />
                        ) : (
                          <ImageIcon size={20} color="#666" />
                        )}
                      </div>
                    )}
                  </View>

                  {/* File name */}
                  <Flex direction="column" flex={1}>
                    <Text
                      fontSize="0.9rem"
                      color="white"
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {file.name}
                    </Text>
                    <Text fontSize="0.75rem" color="#888">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </Flex>

                  {/* Remove button */}
                  <Button
                    variation="link"
                    onClick={() => {
                      // Revoke object URL if it exists
                      const preview = filePreviews[index];
                      if (preview && preview.startsWith('blob:')) {
                        URL.revokeObjectURL(preview);
                      }

                      // Remove the file
                      setFiles(prev => prev.filter((_, i) => i !== index));
                    }}
                    padding="0.25rem"
                  >
                    <X size={16} color="#888" />
                  </Button>
                </Flex>
              ))}
            </Flex>

            {/* Progress bar for all files */}
            {uploading && (
              <View marginTop="1.5rem">
                <Flex justifyContent="space-between" marginBottom="0.5rem">
                  <Text fontSize="0.875rem" color="#AAAAAA">Uploading {uploadedFiles} of {totalFiles} files</Text>
                  <Text fontSize="0.875rem" color="#AAAAAA">{Math.floor((uploadedFiles / totalFiles) * 100)}%</Text>
                </Flex>
                <View
                  backgroundColor="#333"
                  height="0.5rem"
                  borderRadius="full"
                  overflow="hidden"
                >
                  <View
                    backgroundColor="#3B82F6"
                    height="100%"
                    width={`${Math.floor((uploadedFiles / totalFiles) * 100)}%`}
                    style={{ transition: 'width 0.3s ease' }}
                  />
                </View>
              </View>
            )}
          </div>
        )}
      </div>

      {/* Error display */}
      {errors.length > 0 && (
        <View
          backgroundColor={storageLimitExceeded ? "#2A2E3B" : "#3A1D1D"}
          padding="1rem"
          marginTop="1rem"
          borderRadius="4px"
          border={storageLimitExceeded ? "1px solid #3B4664" : "1px solid #732F2F"}
        >
          <div className="flex items-center mb-2">
            {storageLimitExceeded ? (
              <HardDrive size={16} className="text-blue-400 mr-2" />
            ) : (
              <AlertTriangle size={16} className="text-red-400 mr-2" />
            )}
            <Text
              color={storageLimitExceeded ? "#3B82F6" : "#FF6666"}
              fontWeight="medium"
              className="uppercase tracking-wide text-xs"
            >
              {storageLimitExceeded ? "Storage Limit Reached" : "Upload Error"}
            </Text>
          </div>

          {storageLimitExceeded ? (
            <Text
              color="#CCDDFF"
              fontSize="0.85rem"
              className="my-1"
            >
              {userRole === 'CREATOR' ?
                "You've reached your storage limit. Upgrade your storage plan to upload more photos to this album." :
                "This album has reached its storage limit. Please contact the album creator to upgrade their storage plan."}
            </Text>
          ) : (
            errors.map((error, index) => (
              <Text
                key={index}
                color="#FFCCCC"
                fontSize="0.85rem"
                className="my-1"
              >
                {error}
              </Text>
            ))
          )}
        </View>
      )}

      {/* Upload and Cancel buttons */}
      {files.length > 0 && (
        <Flex direction="row" gap="1rem" marginTop="1.5rem" justifyContent="center">
          {storageLimitExceeded && (userRole === 'CREATOR' || userRole === 'creator') ? (
            // Show upgrade button instead of upload button for creators
            <Button
              onClick={handleUpgradeClick}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm uppercase tracking-wider font-medium rounded transition-colors"
            >
              <span className="flex items-center">
                <HardDrive size={16} className="mr-2" />
                {" Upgrade Storage"}
              </span>
            </Button>
          ) : (
            <Button
              onClick={handleUpload}
              isLoading={uploading}
              loadingText="UPLOADING..."
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm uppercase tracking-wider font-medium rounded transition-colors"
              isDisabled={uploading || storageLimitExceeded}
            >
              {uploading ? (
                "UPLOADING..."
              ) : (
                <span className="flex items-center">
                  <Upload size={16} className="mr-2" />
                  Upload {files.length} {files.length === 1 ? 'File' : 'Files'}
                </span>
              )}
            </Button>
          )}

          <Button
            onClick={() => {
              // Clean up object URLs before removing files
              files.forEach(file => {
                if (file.preview && file.preview.startsWith('blob:')) {
                  URL.revokeObjectURL(file.preview);
                }
              });

              setFiles([]);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
            isDisabled={uploading}
            className="px-6 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-sm uppercase tracking-wider font-medium rounded transition-colors"
          >
            Cancel
          </Button>
        </Flex>
      )}
    </View>
  );
};

export default PhotoUpload;